import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "../context/AdminContext";

const AdminConversationsPage = () => {
  const { adminData, fetchConversations } = useAdmin();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all, web, telegram
  const [fetchPage, setFetchPage] = useState(1);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);

  // Simulate fetching all conversations from backend
  useEffect(() => {
    fetchAllConversations();
  }, []);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Scroll detection for infinite loading
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || loading || error) return;

      const scrollElement = document.documentElement;
      const scrollHeight = scrollElement.scrollHeight;
      const scrollTop = scrollElement.scrollTop;
      const clientHeight = scrollElement.clientHeight;

      // Check if scrolled to bottom (with 100px threshold)
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        handleLoadMore();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadingMore, loading, error, fetchPage]);

  const handleLoadMore = async () => {
    if (loadingMore || loading) return;

    setLoadingMore(true);
    try {
      await fetchAllConversations(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const fetchAllConversations = async (isLoadMore = false) => {
    if (!isLoadMore) {
      setLoading(true);
      setError(null);
    }

    try {
      // Replace with actual API call
      let url = process.env.REACT_APP_ADMIN_URL || "/api/admin/conversations";
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ page: fetchPage }),
      });

      const data = await response.json();
      console.log("API Response:", data);

      if (data?.error) {
        setError(data.error);
        console.error("Error fetching conversations:", data.error);
        if (!isLoadMore) {
          setConversations([]); // Clear conversations on error
        }
        setLoading(false);
        return;
      }

      let result = data[0]?.json?.result || [];
      let nextPage = data[0]?.json?.nextPage || fetchPage + 1;

      let lst = result.map((item) => {
        let numChats = item?.json?.chats ? item?.json?.chats.length : 0;
        // Get the channel and normalize it
        let channel = item?.json?.chats?.[0]?.channel || "web";
        // Normalize to lowercase for consistent filtering
        let normalizedStatus = channel.toLowerCase();

        return {
          convId: item?.json?.chats?.[0]?.session_id || "unknown",
          userId: "user123",
          userName: "user",
          title:
            item?.json?.chats?.[numChats - 1]?.message?.content ||
            "Untitled Conversation",
          lastMessage:
            item?.json?.chats?.[0]?.message?.content || "No messages yet",
          messageCount: numChats,
          status: normalizedStatus,
          chatList: item?.json?.chats || [],
          createdAt: item?.json?.chats?.[numChats - 1]?.created_at || "",
          updatedAt: item?.json?.chats?.[0]?.created_at || "",
        };
      });

      if (isLoadMore) {
        setConversations((prev) => [...prev, ...lst]);
      } else {
        setConversations(lst);
      }

      // Debug: Log unique statuses to help with filtering
      const uniqueStatuses = [...new Set(lst.map((c) => c.status))];
      console.log("Unique statuses in conversations:", uniqueStatuses);

      setError(null);

      // Increment page for next load
      if (lst.length > 0) {
        setFetchPage(nextPage);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
      setError(error.message || "Failed to fetch conversations");
      if (!isLoadMore) {
        setConversations([]); // Clear conversations on error
      }
    } finally {
      setLoading(false);
    }
  };

  // Mock data generator - not used anymore, showing error instead
  // const getMockConversations = () => {
  //   return [
  //     {
  //       id: "1",
  //       userId: "user123",
  //       userName: "John Doe",
  //       title: "Temperature sensor inquiry",
  //       lastMessage:
  //         "Can you tell me more about the wireless temperature sensors?",
  //       messageCount: 15,
  //       status: "web",
  //       createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  //       updatedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
  //     },
  //     {
  //       id: "2",
  //       userId: "user456",
  //       userName: "Jane Smith",
  //       title: "Rack monitoring solutions",
  //       lastMessage: "We need monitoring for 50 server racks",
  //       messageCount: 8,
  //       status: "telegram",
  //       createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  //       updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  //     },
  //     {
  //       id: "3",
  //       userId: "user789",
  //       userName: "Mike Johnson",
  //       title: "Power monitoring setup",
  //       lastMessage: "Thank you for the detailed information!",
  //       messageCount: 23,
  //       status: "web",
  //       createdAt: new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString(),
  //       updatedAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
  //     },
  //   ];
  // };

  const cutMessage = (message, length) => {
    const preview = message.content.substring(0, length);
    return preview + (message.content.length > length ? "..." : "");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const messageDay = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    const timeString = date
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();

    if (messageDay.getTime() === today.getTime()) {
      return timeString;
    }

    if (messageDay.getTime() === yesterday.getTime()) {
      return `Yesterday, ${timeString}`;
    }

    const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "long" });
    const year = date.getFullYear();

    const getOrdinal = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    return `${dayName}, ${getOrdinal(day)} ${month} ${year}`;
  };

  const handleViewConversation = (conversationId) => {
    // Find the conversation to pass its data
    const conversation = conversations.find(
      (conv) => conv.convId === conversationId
    );
    if (conversation) {
      // Navigate to admin chat viewing page with conversation data
      navigate(`/chat/${conversationId}`, { state: { conversation } });
    }
  };

  // Filter conversations based on search and status
  const filteredConversations = conversations.filter((conv) => {
    // Search filter
    const matchesSearch =
      searchTerm === "" ||
      conv.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.lastMessage.toLowerCase().includes(searchTerm.toLowerCase());

    // Status filter - ensure proper comparison
    let matchesStatus = false;
    if (filterStatus === "all") {
      matchesStatus = true;
    } else if (filterStatus === "web") {
      // Match if status is "web" or not "telegram"
      matchesStatus =
        conv.status === "web" ||
        (conv.status !== "telegram" && conv.status !== "Telegram");
    } else if (filterStatus === "telegram") {
      // Match if status is "telegram" (case insensitive)
      matchesStatus = conv.status === "telegram" || conv.status === "Telegram";
    }

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen pt-16 sm:pt-20 pb-10 px-3 sm:px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <svg
                className="animate-spin h-10 w-10 mx-auto mb-4 text-blue-500"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className="text-gray-400">Loading conversations...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 sm:pt-20 pb-10 px-3 sm:px-4">
      {/* Error Alert */}
      {error && (
        <div className="fixed top-20 right-4 z-50 animate-fadeIn">
          <div className="glass bg-red-900/50 border border-red-500/50 rounded-lg p-4 max-w-sm shadow-lg shadow-red-500/20">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg
                  className="w-5 h-5 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="flex-grow">
                <h3 className="text-sm font-semibold text-red-300 mb-1">
                  Error Loading Conversations
                </h3>
                <p className="text-xs text-red-200">{error}</p>
                <p className="text-xs text-gray-400 mt-2">
                  Using cached data instead
                </p>
              </div>
              <button
                onClick={() => setError(null)}
                className="flex-shrink-0 text-red-400 hover:text-red-300 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 mb-2">
            All Conversations
          </h1>
          <p className="text-sm sm:text-base text-gray-400">
            Monitor and manage all system conversations
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="glass rounded-lg p-4 border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400">Total Conversations</p>
                <p className="text-2xl font-bold text-white">
                  {conversations.length}
                </p>
              </div>
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="glass rounded-lg p-4 border border-green-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400">Web Channel</p>
                <p className="text-2xl font-bold text-green-400">
                  {conversations.filter((c) => c.status === "web").length}
                </p>
              </div>
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="glass rounded-lg p-4 border border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400">Telegram Channel</p>
                <p className="text-2xl font-bold text-blue-400">
                  {conversations.filter((c) => c.status === "telegram").length}
                </p>
              </div>
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-blue-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.56c-.21 2.27-1.13 7.75-1.6 10.28-.2 1.07-.59 1.43-.96 1.47-.82.08-1.44-.54-2.24-.99-1.24-.71-1.94-1.15-3.14-1.84-1.39-.79-.49-1.23.3-1.94.21-.19 3.85-3.52 3.91-3.82.01-.04.01-.19-.07-.27-.08-.08-.2-.05-.28-.03-.12.03-2.02.89-5.7 2.61-.54.26-1.03.38-1.48.37-.49-.01-1.42-.27-2.11-.5-.85-.28-1.53-.43-1.47-.91.03-.25.37-.51 1.02-.78 3.99-1.74 6.66-2.88 7.99-3.43 3.81-1.57 4.6-1.85 5.11-1.86.11 0 .37.03.53.18.14.12.18.28.2.46-.01.06.01.24 0 .38z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-grow relative">
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-10 rounded-lg bg-gray-800/50 backdrop-blur-md text-white 
                         placeholder-gray-400 focus:outline-none border border-blue-500/30 
                         focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all"
            />
            <svg
              className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 rounded-lg bg-gray-800/50 backdrop-blur-md text-white 
                       focus:outline-none border border-blue-500/30 focus:border-blue-400 
                       focus:ring-2 focus:ring-blue-400/20 transition-all cursor-pointer"
          >
            <option value="all">All channels</option>
            <option value="web">Web</option>
            <option value="telegram">Telegram</option>
          </select>
        </div>

        {/* Conversations List */}
        {error && conversations.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center animate-fadeIn">
            <div
              className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-red-400 to-red-600 
                            rounded-2xl flex items-center justify-center shadow-lg shadow-red-500/30"
            >
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              Failed to Load Conversations
            </h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <button
              onClick={() => {
                setFetchPage(1);
                fetchAllConversations();
              }}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 
                         text-white hover:brightness-125 transition-all duration-300"
            >
              Try Again
            </button>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center animate-fadeIn">
            <div
              className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-400 to-purple-600 
                            rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/30"
            >
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              No Conversations Found
            </h2>
            <p className="text-gray-400">
              {searchTerm
                ? "No conversations match your search criteria"
                : "No conversations in the system yet"}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3 sm:space-y-4">
              {filteredConversations.map((conversation, index) => (
                <div
                  key={conversation.convId || index}
                  onClick={() => handleViewConversation(conversation.convId)}
                  className="group relative glass border border-blue-500/20 rounded-xl p-4 cursor-pointer
                             transition-all duration-300 hover:scale-[1.02] hover:border-blue-400/40
                             hover:shadow-[0_0_30px_rgba(56,189,248,0.3)] animate-fadeIn"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-grow mr-4">
                      <div className="flex items-center space-x-3 mb-1">
                        <h3 className="text-lg font-semibold text-white group-hover:text-blue-300 transition-colors line-clamp-1">
                          {conversation.title}
                        </h3>
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            conversation.status === "web"
                              ? "bg-green-500/20 text-green-300 border border-green-500/30"
                              : conversation.status === "telegram"
                              ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                              : "bg-gray-500/20 text-gray-400 border border-gray-500/30"
                          }`}
                        >
                          {conversation.status === "telegram"
                            ? "Telegram"
                            : conversation.status === "web"
                            ? "Web"
                            : conversation.status}
                        </span>
                      </div>

                      <div className="flex items-center space-x-4 text-sm text-gray-400 mb-2">
                        <span className="flex items-center space-x-1">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                          <span>{conversation.userName}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                            />
                          </svg>
                          <span>{conversation.messageCount} messages</span>
                        </span>
                      </div>

                      <p className="text-sm text-gray-400 line-clamp-1">
                        Last: {conversation.lastMessage}
                      </p>

                      <p className="text-xs text-gray-500 mt-2">
                        Updated: {formatDate(conversation.updatedAt)}
                      </p>
                    </div>

                    <div className="flex items-center">
                      <svg
                        className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Loading More Indicator */}
            {loadingMore && (
              <div className="mt-6 text-center">
                <svg
                  className="animate-spin h-8 w-8 mx-auto text-blue-500"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <p className="text-gray-400 text-sm mt-2">
                  Loading more conversations...
                </p>
              </div>
            )}
          </>
        )}

        {filteredConversations.length > 0 && (
          <div className="mt-6 text-center">
            <p className="text-gray-500 text-xs sm:text-sm">
              Showing {filteredConversations.length} of {conversations.length}{" "}
              conversations
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminConversationsPage;
